import { useForm } from "@inertiajs/react";
import { FormEventHandler } from "react";

// Enhanced validation error types
export type ValidationErrors = Record<string, string | string[]> & {
  base?: string | string[];
};

export interface UseInertiaFormOptions {
  preserveScroll?: boolean;
  preserveState?: boolean;
  onSuccess?: (page: any) => void;
  onError?: (errors: ValidationErrors) => void;
  onFinish?: () => void;
  onStart?: () => void;
  transform?: (data: any) => any;
}

export function useInertiaForm<T extends Record<string, any>>(
  initialData: T,
  options: UseInertiaFormOptions = {},
) {
  const form = useForm<T>(initialData);

  // Enhanced defaults for better UX
  const defaultOptions = {
    preserveScroll: true,
    preserveState: true,
    onStart: () => {
      // Clear previous errors on new submission
      form.clearErrors();
      options.onStart?.();
    },
    onError: (errors: ValidationErrors) => {
      // Enhanced error logging in development
      if (process.env.NODE_ENV === "development") {
        console.warn("Form validation errors:", errors);
      }
      options.onError?.(errors);
    },
    onSuccess: (page: any) => {
      // Auto-reset form on successful submission unless specified otherwise
      if (options.onSuccess) {
        options.onSuccess(page);
      } else {
        form.reset();
      }
    },
    onFinish: options.onFinish,
    transform: options.transform,
    ...options,
  };

  const createSubmitHandler = (
    url: string,
    method: "post" | "put" | "patch" = "post",
  ): FormEventHandler => {
    return (e) => {
      e.preventDefault();

      // Prevent double submission
      if (form.processing) return;

      form[method](url, defaultOptions);
    };
  };

  const createDeleteHandler = (url: string, confirmMessage?: string): FormEventHandler => {
    return (e) => {
      e.preventDefault();

      // Prevent double submission
      if (form.processing) return;

      // Optional confirmation dialog
      if (confirmMessage && !window.confirm(confirmMessage)) {
        return;
      }

      form.delete(url, defaultOptions);
    };
  };

  // Enhanced form utilities
  const hasErrors = (field?: keyof T) => {
    if (field) {
      return !!form.errors[field];
    }
    return Object.keys(form.errors).length > 0;
  };

  const getError = (field: keyof T) => {
    const error = form.errors[field];
    if (Array.isArray(error)) {
      return error[0]; // Return first error message
    }
    return error;
  };

  const getAllErrors = (field: keyof T) => {
    const error = form.errors[field];
    if (Array.isArray(error)) {
      return error;
    }
    return error ? [error] : [];
  };

  return {
    ...form,
    createSubmitHandler,
    createDeleteHandler,
    hasErrors,
    getError,
    getAllErrors,
    // Convenience methods for common operations
    isSubmitting: form.processing,
    canSubmit: !form.processing && !hasErrors(),
  };
}
